import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import { BackupFileModelForStepsHelper } from 'wizards/helpers/backup-file-model-helpers/backup-file-model-for-steps-helper';
import { BackupFilePlanFromStepsHelper } from 'wizards/helpers/backup-file-model-helpers/backup-file-plan-from-steps-helper';
import { BaseBackupPlan } from 'wizards/models/base/base-backup-plan-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { AdvancedFilterStepComponent } from 'wizards/steps/advanced-filter-step/advanced-filter-step.component';
import { AdvancedOptionsStepComponent } from 'wizards/steps/advanced-options-step/advanced-options-step.component';
import { ConsistencyStepComponent } from 'wizards/steps/archive-consistency-step/consistency-step.component';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { NBFRetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.component';
import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SimpleScheduleStepComponent } from 'wizards/steps/simple-schedule-step/simple-schedule-step.component';
import { WhatBackupTreeStepComponent } from 'wizards/steps/what-backup-tree-step/what-backup-tree-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { BackupFileFormModel } from '../../models/backupFile/backup-form-model';
import { WizardsServicesHubService } from '../../services/wizards-services-hub.service';
import { WizardBaseClass } from '../wizard-base.class';

type FileBackupWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'destinationPlusRetention'
  | 'advancedOptions'
  | 'whatToBackUp'
  | 'advancedFilter'
  | 'compressionEncryption'
  | 'fullConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-backup-files-wizard',
  templateUrl: './backup-files-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => BackupFilesWizardComponent)
    }
  ],
  imports: [
    AdvancedFilterStepComponent,
    AdvancedOptionsStepComponent,
    AsyncPipe,
    CommonModule,
    CompressionEncryptionStepComponent,
    ConsistencyStepComponent,
    DestinationPlusRetentionStepComponent,
    FormsModule,
    I18NextPipe,
    NBFRetentionPolicyStepComponent,
    NBFScheduleStepComponent,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SimpleScheduleStepComponent,
    WhatBackupTreeStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent
  ]
})
export class BackupFilesWizardComponent extends WizardBaseClass {
  i18nPipe: I18NextPipe;

  public fromGettingStarted = false;
  public helpLink: WizardHelpLink<FileBackupWizardHelpDocumentationLinks>;

  constructor() {
    const services = inject(WizardsServicesHubService);
    const i18nPipe = inject(I18NextPipe);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.fromGettingStarted = services.mainService.fromGettingStarted;
    this.FormModel = new BackupFileFormModel(this.isNBF, this.isUnix && !this.isNetBuild);

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      if (!this.isEdit && this.isNBF && (!this.isUnix || this.isNetBuild)) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    const formData = BackupFileModelForStepsHelper.BackupModelFromPlan(plan, this.defaultSettings());

    if (this.isRDMode) formData.advancedOptionsFlags = this.FormModel.advancedOptionsFlags;

    this.FormModel = formData;

    // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
    const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
    if (!this.isEdit && this.isNBF && (!this.isUnix || this.isNetBuild) && !rightVersionForSchedule) {
      this.FormModel.scheduleStep.ScheduleType = 'noschedule';
      this.FormModel.scheduleStep.fullBackupData.enabled = false;
    }
  }

  enableVSSHandler(value: boolean): void {
    if (!this.isUnix || this.isNetBuild) {
      this.FormModel.advancedOptionsStep.AlwaysUseVSS = value;
      if (!value) this.FormModel.advancedOptionsStep.UseFastNtfsScan = false;
      this.FormModel.advancedOptionsStep = { ...this.FormModel.advancedOptionsStep };
    }
  }

  offFastNTFSHandler(): void {
    this.FormModel.advancedOptionsStep.UseFastNtfsScan = false;
    this.FormModel.advancedOptionsStep.AlwaysUseVSS = true;
    this.FormModel.advancedOptionsStep = { ...this.FormModel.advancedOptionsStep };
    this.forceNextStepWizard(false, 'advancedOptionsStep', 'Advanced Options');
  }

  protected getPlanForSaving(): BaseBackupPlan {
    const recTypeNumber = +ScheduleType[this.FormModel.scheduleStep.ScheduleType];

    if (
      (!this.isUnix || this.isNetBuild) &&
      !this.isNBF &&
      ((recTypeNumber === ScheduleType.predefined && this.FormModel.simpleScheduleStep.blockLevelBackup) ||
        (recTypeNumber === ScheduleType.recurring &&
          this.FormModel.scheduleAdvancedStep.blockLevel &&
          this.FormModel.scheduleAdvancedStep.blockLevel.Enabled))
    ) {
      this.enableVSSHandler(true);
    }

    return BackupFilePlanFromStepsHelper.getUpdatedPlan(
      this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings(),
      this.isNBF,
      this.isUnix
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  loadInfoHandler() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected loadInfoHandlerFinally() {}

  getRAWHelpLinks(): Record<FileBackupWizardHelpDocumentationLinks, string> {
    let osPrefix = ''; // Default for windows and isRDMode

    if (this.mainService.isLinux) {
      osPrefix = 'linux-';
    }
    if (this.mainService.isMac) {
      osPrefix = 'mac-';
    }

    return {
      welcome: `backup/file/${osPrefix}nbf#1`,
      whereToBackUp: `backup/file/${osPrefix}nbf#2`,
      destinationPlusRetention: `backup/file/${osPrefix}nbf#2`,
      advancedOptions: `backup/file/${osPrefix}nbf#3`,
      whatToBackUp: `backup/file/${osPrefix}nbf#4`,
      advancedFilter: `backup/file/${osPrefix}nbf#5`,
      compressionEncryption: `backup/file/${osPrefix}nbf#6`,
      fullConsistencyCheck: `backup/file/${osPrefix}nbf#7`,
      schedule: `backup/file/${osPrefix}nbf#8`,
      retention: this.isNBF ? `backup/file/${osPrefix}nbf#9` : 'backup/file/legacy#retention',
      prePost: `backup/file/${osPrefix}nbf#10`,
      notification: `backup/file/${osPrefix}nbf#11`
    };
  }
}
